import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, Lock, Mail, Search, X } from "lucide-react";
import AppLiveClock from "../components/ui/AppLiveClock";
import LeagueFilterRail from "../components/p2p/create/LeagueFilterRail";
import MatchRow from "../components/p2p/create/MatchRow";
import CreatePredictionModal from "../components/predictions/create/CreatePredictionModal";
import { fetchP2PCreateLeagues } from "../utils/p2pCreateApi";
import {
  addCommunityPrediction,
  setPredictionsMode,
} from "../redux/predictionsSlice";
import { selectAppIsAuthenticated, selectAppUser, patchAppUser } from "../redux/appAuthSlice";
import { isUserVerified } from "../utils/profileHelpers";
import { syncBondChatForUser } from "../redux/bondchat/generalChatSlice";
import { createPredictionWithAck } from "../redux/predictionSocket";
import {
  filterUpcomingOrLiveFixtures,
  flattenLeaguesToFixtures,
  getLeagueId,
  norm,
  rowMatchesQuery,
  searchTokensFromQuery,
} from "../utils/p2pCreateHelpers";
import { sortLeaguesByPriority } from "../utils/leaguePriority";
import "../betdetail.css";
import "../p2p-create.css";
import "../bettors-desk.css";
import "../predictions-create.css";

const CreatePrediction = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const isAuthenticated = useSelector(selectAppIsAuthenticated);
  const location = useLocation();
  const navigate = useNavigate();
  const [leaguesRaw, setLeaguesRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [modalTarget, setModalTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const canCreate = isAuthenticated && isUserVerified(user);
  const hasQuery = Boolean(String(search || "").trim());
  const isSearchExpanded = searchOpen || hasQuery;

  const openSearch = useCallback(() => setSearchOpen(true), []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearch("");
  }, []);

  const toggleSearch = useCallback(() => {
    if (isSearchExpanded && !hasQuery) {
      setSearchOpen(false);
      return;
    }
    if (isSearchExpanded && hasQuery) {
      closeSearch();
      return;
    }
    openSearch();
  }, [closeSearch, hasQuery, isSearchExpanded, openSearch]);

  useEffect(() => {
    if (user?._id) dispatch(syncBondChatForUser(user._id));
  }, [user?._id, dispatch]);

  useEffect(() => {
    if (!toast || toast.type === "pending") return undefined;
    const t = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (!isSearchExpanded) return undefined;

    const handlePointerDown = (event) => {
      if (searchWrapRef.current?.contains(event.target)) return;
      if (!hasQuery) setSearchOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (hasQuery) setSearch("");
      else setSearchOpen(false);
      searchInputRef.current?.blur();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSearchExpanded, hasQuery]);

  const handleSearchBlur = () => {
    window.setTimeout(() => {
      if (
        !String(search || "").trim() &&
        !searchWrapRef.current?.contains(document.activeElement)
      ) {
        setSearchOpen(false);
      }
    }, 120);
  };

  useEffect(() => {
    const incoming = location.state?.fixture;
    if (!incoming || !canCreate) return;
    setModalTarget({
      fixture: incoming,
      league: location.state?.league || null,
    });
    navigate("/app/predictions/create", { replace: true, state: null });
  }, [location.state, navigate, canCreate]);

  useEffect(() => {
    if (!canCreate) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchP2PCreateLeagues(10);
        if (!cancelled) setLeaguesRaw(data);
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load leagues");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canCreate]);

  const openModal = ({ fixture, league }) => {
    if (!canCreate) return;
    setModalTarget({ fixture, league });
  };

  const closeModal = () => setModalTarget(null);

  const handleTipSubmit = useCallback(
    async (payload) => {
      setToast({ type: "pending", message: "Publishing tip…" });
      try {
        const response = await createPredictionWithAck(payload);
        if (typeof response?.coinBalance === "number") {
          dispatch(patchAppUser({ COIN: response.coinBalance }));
        }
        if (response?.prediction?._id) {
          dispatch(addCommunityPrediction(response.prediction));
          dispatch(setPredictionsMode("community"));
        }
        navigate("/app/predictions", {
          replace: true,
          state: {
            toast: {
              type: "success",
              message: response?.message || "Your tip was published successfully.",
            },
          },
        });
      } catch (err) {
        setToast({
          type: "error",
          message: err.message || "Could not publish prediction.",
        });
      }
    },
    [dispatch, navigate]
  );

  const allRows = useMemo(
    () => flattenLeaguesToFixtures(leaguesRaw),
    [leaguesRaw]
  );

  const leagueOptions = useMemo(
    () =>
      sortLeaguesByPriority(
        leaguesRaw.map((league) => ({
          id: getLeagueId(league),
          apiId: league?.id ?? null,
          name: league?.leagueName || "League",
          country: league?.countryName || "",
          logo: league?.leagueLogo || null,
          count: filterUpcomingOrLiveFixtures(league?.fixtures || []).length,
        }))
      ),
    [leaguesRaw]
  );

  const activeLeague = useMemo(
    () => leagueOptions.find((l) => l.id === selectedLeagueId) || null,
    [leagueOptions, selectedLeagueId]
  );

  const tokens = useMemo(() => searchTokensFromQuery(search), [search]);

  const visibleRows = useMemo(() => {
    let rows = allRows;
    if (selectedLeagueId) {
      rows = rows.filter((r) => r.league?.id === selectedLeagueId);
    }
    if (tokens.length) {
      rows = rows.filter((r) => rowMatchesQuery(r, tokens));
    }
    return rows;
  }, [allRows, selectedLeagueId, tokens]);

  const isSearching = norm(search).length > 0;

  if (!isAuthenticated) {
    return (
      <div className="prdc-page">
        <header className="pcr-header">
          <h1 className="pcr-header-title">Create prediction</h1>
        </header>
        <div className="prdc-gate">
          <Lock size={28} />
          <h2>Sign in required</h2>
          <p>Log in to publish community predictions.</p>
          <Link to="/app/login" state={{ from: "/app/predictions/create" }} className="prdc-gate-btn">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  if (!isUserVerified(user)) {
    return (
      <div className="prdc-page">
        <header className="pcr-header">
          <h1 className="pcr-header-title">Create prediction</h1>
        </header>
        <div className="prdc-gate">
          <Mail size={28} />
          <h2>Verify your email</h2>
          <p>Verify your email address before creating predictions.</p>
          <p className="prdc-gate-sub">You can post up to 5 predictions per UTC day.</p>
          <Link to="/app/verify" className="prdc-gate-btn">
            Verify email
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="prdc-page">
      <header ref={searchWrapRef} className="pcr-header prdc-header">
        <div className="pcr-header-row">
          <div className="pcr-header-lead">
            <h1 className="pcr-header-title">Create prediction</h1>
          </div>
          <div className="pcr-header-clock-wrap app-chrome-clock-center">
            <AppLiveClock className="app-chrome-clock" />
          </div>
          <div className="pcr-header-actions">
            <button
              type="button"
              className={`pcr-chrome-search-toggle${
                isSearchExpanded ? " pcr-chrome-search-toggle-on" : ""
              }`}
              onClick={toggleSearch}
              aria-label={isSearchExpanded ? "Close search" : "Search teams"}
              aria-expanded={isSearchExpanded}
            >
              {isSearchExpanded ? (
                <X size={14} strokeWidth={2.3} aria-hidden />
              ) : (
                <Search size={14} strokeWidth={2.3} aria-hidden />
              )}
            </button>
          </div>
        </div>
        {isSearchExpanded ? (
          <div className="pcr-search-panel">
            <Search size={14} aria-hidden className="pcr-chrome-search-icon" />
            <input
              ref={searchInputRef}
              type="search"
              className="pcr-chrome-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={handleSearchBlur}
              placeholder="Search teams…"
              aria-label="Search teams"
            />
            {hasQuery ? (
              <button
                type="button"
                className="pcr-chrome-search-clear"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            ) : null}
          </div>
        ) : null}
      </header>

      {toast ? (
        <div
          className={`p2p-toast p2p-toast-${toast.type || "success"}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === "pending" ? (
            <Loader2 size={16} className="pcr-spin" aria-hidden />
          ) : null}
          {toast.message}
        </div>
      ) : null}

      <div className="pcr-split">
        {!error ? (
          <LeagueFilterRail
            leagues={loading ? [] : leagueOptions}
            selectedId={selectedLeagueId}
            onSelect={setSelectedLeagueId}
            totalMatches={loading ? 0 : allRows.length}
            loading={loading}
          />
        ) : null}

        <main className="pcr-games">
          <div className="pcr-games-head">
            <h2 className="pcr-games-title">
              {activeLeague ? activeLeague.name : "All leagues"}
            </h2>
            <span className="pcr-games-count tabular-nums" aria-label={`${visibleRows.length} fixtures`}>
              {visibleRows.length}
            </span>
          </div>

          {loading ? (
            <div className="pcr-state">
              <Loader2 size={22} className="pcr-spin" />
              <p>Loading leagues &amp; matches…</p>
            </div>
          ) : null}

          {!loading && error ? (
            <div className="pcr-state pcr-state-err">
              <p>{error}</p>
            </div>
          ) : null}

          {!loading && !error && visibleRows.length === 0 ? (
            <div className="pcr-state">
              <p className="pcr-empty-title">
                {isSearching ? "No teams match your search" : "No upcoming matches"}
              </p>
              <p className="pcr-empty-sub">
                {isSearching
                  ? "Try another team name or clear the search."
                  : selectedLeagueId
                  ? "This league has no fixtures right now. Try another league."
                  : "Check back soon. New fixtures are added regularly."}
              </p>
            </div>
          ) : null}

          {!loading && !error && visibleRows.length > 0 ? (
            <div className="pcr-games-list">
              {visibleRows.map((row) => (
                <MatchRow
                  key={row.fixture._id}
                  row={row}
                  hideLeague={Boolean(selectedLeagueId)}
                  onChallenge={openModal}
                  ctaLabel="Predict"
                />
              ))}
            </div>
          ) : null}
        </main>
      </div>

      <CreatePredictionModal
        open={Boolean(modalTarget)}
        fixture={modalTarget?.fixture}
        league={modalTarget?.league}
        onClose={closeModal}
        onSubmit={handleTipSubmit}
      />
    </div>
  );
};

export default CreatePrediction;
